import { db, auth } from '@/firebase/config'
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
  deleteField
} from 'firebase/firestore'
import {
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth'
import { createLog } from './logService'
import { encryptFields, decryptFields } from './encryptionService'
import {
  getSubscriptionByUserId,
  cancelParentSubscription,
  resumeParentSubscription
} from './paymentService'

const ALLOWED_KEYS_FOR_UPDATE = new Set([
  'firstName',
  'lastName',
  'pendingEmail',
  'updatedAt',
  'role',
  'agreeToTerms',
  'country',
  'region',
  'receiveUpdates',
  'lastDateOfConsent',
  'parentConsent',
  'status',
  'emailVerified',
  'emailVerifiedAt',
  'consentSettings',
  'schoolId',
  'planType',
  'hasSubscription',
  'isOnboarded',
  'isSchoolOnboarded',
  'heardAboutUs',
  'phone',
  'parentPin',
  'parentPinSetDate',
  'passwordLastChanged',
  'anon_id'
])

export function getCurrentUserId() {
  const uid = auth.currentUser?.uid
  if (!uid) throw new Error('User not authenticated')
  return uid
}

export const updateUser = async (uid, data) => {
  const currentAuthUid = auth.currentUser?.uid
  if (!currentAuthUid) {
    throw new Error(
      'Unauthorized: you must be logged in to update your profile'
    )
  }
  if (currentAuthUid !== uid) {
    throw new Error('Unauthorized: you can only update your own profile')
  }

  const filteredData = {}
  for (const key of Object.keys(data)) {
    if (ALLOWED_KEYS_FOR_UPDATE.has(key)) {
      filteredData[key] = data[key]
    }
  }

  const userRef = doc(db, 'users', currentAuthUid)
  const updateData = {
    ...filteredData,
    updatedAt: serverTimestamp()
  }

  if ('email' in filteredData) {
    updateData.pendingEmail = filteredData.pendingEmail ?? null
  }

  const encryptedData = encryptFields(updateData)

  await updateDoc(userRef, encryptedData)

  const updatedUserData = await getUserByUid(currentAuthUid)

  await createLog({
    userId: currentAuthUid,
    collectionName: 'users',
    data: filteredData,
    schoolId: updatedUserData.schoolId ?? null,
    action: 'UPDATE_USER'
  })

  return updatedUserData
}

export const getUserByUid = async (uid) => {
  const userRef = doc(db, 'users', uid)
  const docSnap = await getDoc(userRef)
  const userData = docSnap.data()
  const decryptedData = decryptFields(userData)
  return decryptedData
}

export const getTrustedAdults = async (uid) => {
  const userRef = doc(db, 'trustedAdults', uid)
  const docSnap = await getDoc(userRef)
  return docSnap.data()
}

export const getUserSchool = async (schoolId) => {
  if (!schoolId) {
    return null
  }
  const schoolRef = doc(db, 'schools', schoolId)
  const docSnap = await getDoc(schoolRef)
  if (!docSnap.exists()) {
    return null
  }
  const decryptedData = decryptFields(docSnap.data())
  return decryptedData
}

export const downloadUserData = async (userId) => {
  try {
    // Security check
    if (!userId || !auth.currentUser) {
      throw new Error('Unable to download data: User not authenticated')
    }

    // Verify the requesting user matches the requested data
    if (auth.currentUser.uid !== userId) {
      throw new Error('You can only download your own data')
    }

    // Use existing function to get user data
    let userData = null
    try {
      userData = await getUserByUid(userId)
      if (!userData) {
        throw new Error('User data not found')
      }
    } catch (error) {
      console.error('Error getting user data:', error)
      throw new Error('Failed to retrieve user data')
    }

    // Get trusted adults data (might not exist)
    let trustedAdultsData = null
    try {
      trustedAdultsData = await getTrustedAdults(userId)
    } catch (error) {
      console.error('No trusted adults data found', error)
    }

    // Get school data (if exists) - sanitized
    let schoolData = null
    if (userData.schoolId) {
      try {
        const rawSchoolData = await getUserSchool(userData.schoolId)
        if (rawSchoolData) {
          // Sanitize school data to remove sensitive information
          schoolData = {
            id: userData.schoolId,
            name: rawSchoolData.name,
            address: rawSchoolData.address,
            contact: rawSchoolData.contact,
            // Remove administrative details and other sensitive fields
            admins: undefined,
            apiKeys: undefined,
            billingInfo: undefined
          }
        }
      } catch (error) {
        console.error('No school data found', error)
      }
    }

    // Get payment history (if applicable)
    const paymentHistory = []
    if (userData.role === 'parent') {
      try {
        const paymentsRef = collection(db, 'payments')
        const q = query(paymentsRef, where('userId', '==', userId))
        const querySnapshot = await getDocs(q)

        querySnapshot.forEach((doc) => {
          const paymentData = doc.data()
          // Sanitize payment data to remove sensitive details
          paymentHistory.push({
            id: doc.id,
            amount: paymentData.amount,
            date: paymentData.date,
            status: paymentData.status,
            description: paymentData.description,
            // Remove payment method details
            paymentMethod: paymentData.paymentMethod
              ? {
                  type: paymentData.paymentMethod.type,
                  last4: paymentData.paymentMethod.last4
                }
              : undefined
          })
        })
      } catch (_error) {
        // No payment history found
      }
    }

    // Get student quizzes data
    const studentQuizzes = []
    try {
      const studentQuizzesRef = collection(db, 'studentQuizzes')
      const q = query(studentQuizzesRef, where('studentId', '==', userId))
      const querySnapshot = await getDocs(q)

      querySnapshot.forEach((doc) => {
        const quizData = doc.data()
        // Sanitize quiz data to remove sensitive information
        studentQuizzes.push({
          id: doc.id,
          quizTitle: quizData.quizTitle,
          status: quizData.status,
          createdAt: quizData.createdAt,
          dueDate: quizData.dueDate,
          mission: quizData.mission,
          score: quizData.score,
          isExpired: quizData.isExpired,
          questions: quizData.questions,
          answers: quizData.answers,
          // Remove sensitive fields
          authorEmail: undefined,
          authorId: undefined
        })
      })
    } catch (_error) {
      // No student quizzes found
    }

    // Get messages data - only those directly related to the user
    const messages = []
    try {
      const messagesRef = collection(db, 'messages')
      const q = query(
        messagesRef,
        where('participants', 'array-contains', userId)
      )
      const querySnapshot = await getDocs(q)

      querySnapshot.forEach((doc) => {
        const messageData = doc.data()
        messages.push({
          id: doc.id,
          content: messageData.content,
          timestamp: messageData.timestamp,
          subject: messageData.subject,
          isRead: messageData.isRead,
          // Include only necessary participant info
          sender: {
            id: messageData.sender.id,
            name: messageData.sender.name,
            role: messageData.sender.role
          },
          // Filter out detailed info about other participants
          recipients: messageData.recipients?.map((r) => ({
            id: r.id,
            name: r.name,
            role: r.role
          }))
        })
      })
    } catch (_error) {
      // No messages found
    }

    // Get class data - sanitized to protect other students' information
    const classes = []
    if (userData.classId) {
      try {
        const classRef = doc(db, 'classes', userData.classId)
        const classSnap = await getDoc(classRef)

        if (classSnap.exists()) {
          const classData = classSnap.data()

          // Sanitize class data to protect privacy
          const sanitizedClassData = {
            id: classSnap.id,
            className: classData.className,
            title: classData.title,
            grade: classData.grade,
            description: classData.description,
            unit: classData.unit,
            nextClass: classData.nextClass,
            classDaysFormatted: classData.classDaysFormatted,
            startDate: classData.startDate,
            formattedStartDate: classData.formattedStartDate,

            // Filter to include only current user's information
            students: classData.students
              ? classData.students.filter(
                  (s) =>
                    s.studentId === userData.studentId ||
                    s.email === userData.email
                )
              : [],

            // Include only upcoming missions (next 3)
            missions: classData.missions ? classData.missions.slice(0, 3) : [],

            // Remove sensitive management information
            createdBy: undefined,
            enableAIChatroom: undefined
          }

          classes.push(sanitizedClassData)
        }
      } catch (_error) {
        // No class data found
      }
    }

    // Get incident reports - only if they involve this user
    const incidentReports = []
    try {
      const reportsRef = collection(db, 'incidentReports')
      const q = query(reportsRef, where('userId', '==', userId))
      const querySnapshot = await getDocs(q)

      querySnapshot.forEach((doc) => {
        const reportData = doc.data()
        // Sanitize report data to protect others' privacy
        incidentReports.push({
          id: doc.id,
          type: reportData.type,
          date: reportData.date,
          status: reportData.status,
          summary: reportData.summary,
          // Remove sensitive details about other users
          reportedBy:
            reportData.reportedBy === userId
              ? reportData.reportedBy
              : undefined,
          otherInvolved: undefined
        })
      })
    } catch (_error) {
      // No incident reports found
    }

    // Get user activity logs - only user-relevant logs
    const logs = []
    try {
      const logsRef = collection(db, 'logs')
      const q = query(logsRef, where('userId', '==', userId))
      const querySnapshot = await getDocs(q)

      querySnapshot.forEach((doc) => {
        logs.push({
          id: doc.id,
          action: doc.data().action,
          createdAt: doc.data().createdAt,
          collection: doc.data().collection,
          // Remove detailed data payload
          data: {}
        })
      })
    } catch (_error) {
      // No logs found
    }

    // Combine all data
    const completeUserData = {
      personalInfo: {
        ...userData,
        // Remove sensitive information
        password: undefined,
        authToken: undefined
      },
      trustedAdults: trustedAdultsData,
      school: schoolData,
      paymentHistory: paymentHistory,
      // Additional data sections
      studentQuizzes: studentQuizzes,
      messages: messages,
      classes: classes,
      incidentReports: incidentReports,
      logs: logs,
      exportDate: new Date().toISOString()
    }

    // Log download activity
    try {
      await createLog({
        userId: auth.currentUser.uid,
        collectionName: 'users',
        action: 'DOWNLOAD_USER_DATA',
        schoolId: 'system', // Explicitly use 'system' to avoid schoolName being undefined
        data: {},
        visibility: 'all' // Mark this log as visible to all (admin and user)
      })
    } catch (error) {
      console.error('Error logging download activity:', error)
      // Continue execution, don't interrupt the download process
    }

    return completeUserData
  } catch (error) {
    console.error('Error downloading user data:', error)
    throw new Error('Failed to download user data: ' + error.message)
  }
}

export const deleteUserAccount = async (userId, password) => {
  try {
    if (!userId || !auth.currentUser) {
      throw new Error('Unable to delete account: User not authenticated')
    }

    if (auth.currentUser.uid !== userId) {
      throw new Error('You can only delete your own account')
    }
    const user = auth.currentUser

    if (!user.email) {
      throw new Error(
        'Unable to authenticate: No email associated with account'
      )
    }

    try {
      const credential = EmailAuthProvider.credential(user.email, password)
      await reauthenticateWithCredential(user, credential)
    } catch (error) {
      console.error('Reauthentication failed:', error)
      throw new Error('Incorrect password or authentication failed')
    }

    const idToken = await auth.currentUser.getIdToken(true)

    let userData = null
    try {
      userData = await getUserByUid(userId)
      if (!userData) {
        throw new Error('User data not found')
      }
    } catch (error) {
      console.error('Error getting user data for deletion:', error)
      throw new Error('Failed to retrieve user data for deletion')
    }

    // Step 1: Cancel subscription first if user has one (from collection or user doc)
    let cancelSource = null
    try {
      let stripeSubscriptionId = null
      let subscriptionStatus = null

      const subscriptionResult = await getSubscriptionByUserId(userId)
      if (
        subscriptionResult.success &&
        subscriptionResult.activeSubscription &&
        subscriptionResult.activeSubscription.stripeSubscriptionId
      ) {
        const activeSub = subscriptionResult.activeSubscription
        if (
          (activeSub.status === 'active' || activeSub.status === 'trialing') &&
          activeSub.status !== 'cancel_at_period_end' &&
          !activeSub.cancelAtPeriodEnd
        ) {
          stripeSubscriptionId = activeSub.stripeSubscriptionId
          subscriptionStatus = activeSub.status
          cancelSource = 'subscriptions_collection'
        }
      }

      if (
        !stripeSubscriptionId &&
        userData?.subscription?.stripeSubscriptionId
      ) {
        const sub = userData.subscription
        if (
          (sub.status === 'active' || sub.status === 'trialing') &&
          sub.status !== 'cancel_at_period_end' &&
          !sub.cancelAtPeriodEnd
        ) {
          stripeSubscriptionId = sub.stripeSubscriptionId
          subscriptionStatus = sub.status
          cancelSource = 'user_doc_fallback'
        }
      }

      if (stripeSubscriptionId && subscriptionStatus) {
        console.info('[DeleteAccount] Cancelling subscription', {
          source: cancelSource,
          stripeSubscriptionId: stripeSubscriptionId?.slice(0, 20) + '...',
          status: subscriptionStatus
        })
        await cancelParentSubscription(
          stripeSubscriptionId,
          subscriptionStatus,
          idToken
        )
        console.info('[DeleteAccount] Subscription cancelled via Stripe', {
          source: cancelSource
        })
      } else {
        cancelSource = 'none'
        console.info('[DeleteAccount] No subscription to cancel', {
          hadFromCollection: !!(
            subscriptionResult.success &&
            subscriptionResult.activeSubscription?.stripeSubscriptionId
          ),
          hadFromUserDoc: !!userData?.subscription?.stripeSubscriptionId
        })
      }
    } catch (error) {
      console.error('[DeleteAccount] Error cancelling subscription:', error)
      cancelSource = 'error'
      // Don't throw error - continue with account deletion even if subscription cancellation fails
    }

    // Step 2: Calculate dates for account deletion
    const now = new Date()
    const deletionDate = new Date(now)
    deletionDate.setDate(deletionDate.getDate() + 60) // 60 days from now
    const deletionTimestamp = Timestamp.fromDate(deletionDate)

    // Step 3: Mark account as deleted
    try {
      const userRef = doc(db, 'users', userId)
      const updateData = {
        status: 'deleted',
        accountTobeDeletedDate: serverTimestamp(),
        accountDeletionDate: deletionTimestamp,
        updatedAt: serverTimestamp()
      }

      const encryptedData = encryptFields(updateData)
      await updateDoc(userRef, encryptedData)
    } catch (error) {
      console.error('Error updating user document:', error)
      throw new Error('Failed to mark account as deleted')
    }

    // Step 4: Log the account deletion action
    try {
      await createLog({
        userId: userId,
        collectionName: 'users',
        action: 'MARK_ACCOUNT_AS_DELETED',
        schoolId: 'system',
        data: { subscriptionCancelSource: cancelSource }
      })
    } catch (error) {
      console.error('Error logging account deletion:', error)
    }

    return true
  } catch (error) {
    console.error('Error deleting account:', error)
    throw new Error(error.message || 'Failed to delete account')
  }
}

/**
 * Reactivates a deleted account and handles subscription
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} Reactivation result
 */
export const reactivateAccount = async (userId) => {
  try {
    if (!userId || !auth.currentUser) {
      throw new Error('User not authenticated')
    }

    if (auth.currentUser.uid !== userId) {
      throw new Error('You can only reactivate your own account')
    }

    // Get user data to check status
    const userData = await getUserByUid(userId)
    if (!userData) {
      throw new Error('User data not found')
    }

    // Check if account is deleted
    if (userData.status !== 'deleted') {
      return { success: true, message: 'Account is already active' }
    }

    // Check accountDeletionDate to see if 60 days have passed
    let accountDeletionDate = null
    if (userData.accountDeletionDate) {
      if (userData.accountDeletionDate.toDate) {
        accountDeletionDate = userData.accountDeletionDate.toDate()
      } else if (userData.accountDeletionDate instanceof Date) {
        accountDeletionDate = userData.accountDeletionDate
      } else {
        accountDeletionDate = new Date(userData.accountDeletionDate)
      }
    }

    const now = new Date()
    const isPastDeletionDate = accountDeletionDate && accountDeletionDate < now

    const getPeriodEndDate = (sub) => {
      const end = sub?.currentPeriodEnd ?? sub?.accessDate
      if (!end) return null
      if (end.toDate) return end.toDate()
      if (end instanceof Date) return end
      if (typeof end === 'number') return new Date(end * 1000)
      return new Date(end)
    }
    const isPeriodStillActive = (sub) => {
      const end = getPeriodEndDate(sub)
      return end && end > now
    }

    const subscriptionResult = await getSubscriptionByUserId(userId)
    let subscriptionHandled = false
    let hasSubscription = userData.hasSubscription || false
    let resumeOutcome = null

    let activeSub =
      subscriptionResult.success && subscriptionResult.activeSubscription
        ? subscriptionResult.activeSubscription
        : null
    let subscriptionSource = activeSub?.stripeSubscriptionId
      ? 'subscriptions_collection'
      : null

    if (
      !activeSub?.stripeSubscriptionId &&
      userData?.subscription?.stripeSubscriptionId
    ) {
      activeSub = userData.subscription
      subscriptionSource = 'user_doc_fallback'
    }

    if (activeSub?.stripeSubscriptionId) {
      const isCancelAtPeriodEnd =
        activeSub.status === 'cancel_at_period_end' ||
        activeSub.cancelAtPeriodEnd
      const canResume =
        (isCancelAtPeriodEnd || activeSub.status === 'trialing') &&
        !isPastDeletionDate &&
        isPeriodStillActive(activeSub)

      if (canResume) {
        console.info('[ReactivateAccount] Resuming subscription', {
          source: subscriptionSource,
          stripeSubscriptionId:
            activeSub.stripeSubscriptionId?.slice(0, 20) + '...',
          status: activeSub.status
        })
        try {
          await resumeParentSubscription(activeSub.stripeSubscriptionId)
          resumeOutcome = 'stripe_resumed'
          console.info('[ReactivateAccount] Subscription resumed via Stripe', {
            source: subscriptionSource
          })
        } catch (error) {
          resumeOutcome = 'fallback_after_resume_failed'
          console.warn(
            '[ReactivateAccount] Resume failed, keeping hasSubscription true until period end',
            {
              source: subscriptionSource,
              error: error?.message
            }
          )
        }
        subscriptionHandled = true
        hasSubscription = true
      } else if (
        activeSub.status === 'cancelled' ||
        activeSub.status === 'canceled' ||
        isPastDeletionDate ||
        !isPeriodStillActive(activeSub)
      ) {
        resumeOutcome = 'not_resumable'
        console.info('[ReactivateAccount] Subscription not resumable', {
          source: subscriptionSource,
          reason: isPastDeletionDate
            ? 'past_deletion_date'
            : !isPeriodStillActive(activeSub)
            ? 'period_ended'
            : 'cancelled',
          hasSubscription: false
        })
        hasSubscription = false
        subscriptionHandled = true
      } else if (
        activeSub.status === 'active' ||
        activeSub.status === 'trialing' ||
        isCancelAtPeriodEnd
      ) {
        resumeOutcome = 'already_active_or_period_end'
        console.info(
          '[ReactivateAccount] Subscription already active or access until period end',
          {
            source: subscriptionSource,
            status: activeSub.status,
            hasSubscription: true
          }
        )
        hasSubscription = true
        subscriptionHandled = true
      }
    } else {
      resumeOutcome = 'user_doc_only'
      hasSubscription = !!(
        userData?.subscription?.stripeSubscriptionId &&
        isPeriodStillActive(userData.subscription)
      )
      subscriptionHandled = true
      console.info(
        '[ReactivateAccount] No subscription from collection; used user doc only',
        {
          hasSubscription,
          periodStillActive: !!(
            userData?.subscription?.stripeSubscriptionId &&
            isPeriodStillActive(userData.subscription)
          )
        }
      )
    }

    // Update user document to reactivate account
    const userRef = doc(db, 'users', userId)

    // Prepare update data - fields to delete must be handled separately
    const updateData = {
      status: 'active',
      updatedAt: serverTimestamp()
    }

    // Update hasSubscription if subscription was handled
    if (subscriptionHandled) {
      updateData.hasSubscription = hasSubscription
    }

    // Encrypt the data that needs encryption
    const encryptedData = encryptFields(updateData)

    // Add deleteField() for fields that need to be removed (these can't be encrypted)
    encryptedData.accountTobeDeletedDate = deleteField()
    encryptedData.accountDeletionDate = deleteField()

    await updateDoc(userRef, encryptedData)

    // Log the reactivation
    try {
      await createLog({
        userId: userId,
        collectionName: 'users',
        action: 'REACTIVATE_ACCOUNT',
        schoolId: 'system',
        data: {
          subscriptionResumed: subscriptionHandled && hasSubscription,
          hasSubscription: hasSubscription,
          subscriptionSource: subscriptionSource ?? 'none',
          resumeOutcome: resumeOutcome ?? 'none'
        }
      })
    } catch (error) {
      console.error('Error logging account reactivation:', error)
    }

    return {
      success: true,
      subscriptionResumed: subscriptionHandled && hasSubscription,
      hasSubscription: hasSubscription
    }
  } catch (error) {
    console.error('Error reactivating account:', error)
    throw new Error(error.message || 'Failed to reactivate account')
  }
}

/**
 * Creates or updates a user profile document in Firestore
 * @param {string} userId - The user's UID
 * @param {Object} profileData - The profile data to save
 * @returns {Promise<void>}
 */
export const saveUserProfile = async (userId, profileData) => {
  const currentAuthUid = auth.currentUser?.uid
  if (!currentAuthUid) {
    throw new Error('Unauthorized: you must be logged in to save your profile')
  }
  if (currentAuthUid !== userId) {
    throw new Error('Unauthorized: you can only save your own profile')
  }
  try {
    const userDocRef = doc(db, 'users', currentAuthUid)
    const docSnapshot = await getDoc(userDocRef)

    const updatedData = {
      ...profileData,
      updatedAt: serverTimestamp()
    }

    const encryptedData = encryptFields(updatedData)

    if (!docSnapshot.exists()) {
      encryptedData.createdAt = serverTimestamp()
      await setDoc(userDocRef, encryptedData)
    } else {
      await updateDoc(userDocRef, encryptedData)
    }

    await createLog({
      userId: currentAuthUid,
      collectionName: 'users',
      data: profileData,
      schoolId: profileData.schoolId ?? null,
      action: 'UPDATE_PROFILE'
    })
  } catch (error) {
    console.error('Error saving profile data:', error)
    throw new Error('Failed to save profile data')
  }
}

/**
 * Fetches a user's profile data from Firestore
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} The user's profile data
 */
export const fetchUserProfile = async (userId) => {
  const currentAuthUid = auth.currentUser?.uid
  if (currentAuthUid && currentAuthUid !== userId) {
    throw new Error('Unauthorized: you can only fetch your own profile')
  }
  try {
    const userDocRef = doc(db, 'users', userId)
    const docSnapshot = await getDoc(userDocRef)

    if (docSnapshot.exists()) {
      const userData = docSnapshot.data()
      return decryptFields(userData)
    } else {
      return null
    }
  } catch (error) {
    console.error('Error fetching profile data:', error)
    throw new Error('Failed to fetch profile data')
  }
}
